/**
 * src/documents/draftStorage.js
 *
 * Per-document draft autosave to localStorage. Used by DocumentEditor to
 * preserve in-flight edits when the browser tab is closed before the user
 * clicks Save. Drafts are scoped by tenant + user + document id so a shared
 * device with multiple tenant users never surfaces one user's draft to
 * another, and cleared on every successful real save.
 *
 * Tenants already live on separate origins (subdomain-per-tenant), which
 * isolates localStorage at the browser level; the tenantId segment here is
 * defense-in-depth for custom-domain / env-portal setups where that origin
 * isolation doesn't apply.
 *
 * Drafts are device-local: localStorage does not sync across browsers or
 * machines. That is deliberate. A server-side mirror in the shared tenant
 * state (state.documentDrafts) was retired in CDEA-01 (GH #6952, option B):
 * it was keyed by docId alone, so every user's in-progress draft was
 * readable by every other user in the tenant, and a private whole-document
 * copy per user is the opposite of the concurrent-editing destination
 * (docs/concurrent-document-editing-session-plan.md). Cross-device restore
 * went with it; same-device restore is unchanged.
 *
 * The 5MB localStorage quota is shared across all keys for the origin, so
 * we cap each draft at ~4MB and silently drop on quota errors.
 */

const KEY_PREFIX = 'seasuite_doc_draft_';
const MAX_BYTES = 4_000_000; // ~4MB per draft
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function scopePrefix(tenantId, userId) {
  return `${KEY_PREFIX}${tenantId || 'na'}_${userId || 'anon'}_`;
}

export function draftKey(tenantId, userId, docId) {
  return `${scopePrefix(tenantId, userId)}${docId}`;
}

export function saveDraft(tenantId, userId, docId, payload) {
  if (!docId) return false;
  try {
    const json = JSON.stringify(payload);
    if (json.length > MAX_BYTES) return false;
    localStorage.setItem(draftKey(tenantId, userId, docId), json);
    return true;
  } catch (_e) {
    return false;
  }
}

export function loadDraft(tenantId, userId, docId) {
  if (!docId) return null;
  try {
    const raw = localStorage.getItem(draftKey(tenantId, userId, docId));
    if (!raw) return null;
    const draft = JSON.parse(raw);
    if (!draft || !draft.savedAt) return null;
    if (Date.now() - new Date(draft.savedAt).getTime() > MAX_AGE_MS) {
      clearDraft(tenantId, userId, docId);
      return null;
    }
    return draft;
  } catch (_e) {
    return null;
  }
}

export function clearDraft(tenantId, userId, docId) {
  if (!docId) return;
  try {
    localStorage.removeItem(draftKey(tenantId, userId, docId));
  } catch (_e) {
    /* ignore */
  }
}

// Removes every locally-stored draft for this tenant+user, regardless of
// docId. Called on logout so a subsequent user on the same device/browser
// never sees a prior user's unsaved draft content (F-37 follow-up).
export function clearAllDraftsForUser(tenantId, userId) {
  try {
    const prefix = scopePrefix(tenantId, userId);
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) toRemove.push(key);
    }
    toRemove.forEach((key) => localStorage.removeItem(key));
  } catch (_e) {
    /* ignore */
  }
}

// Mount-time garbage collection: drops drafts older than MAX_AGE_MS, and
// drops drafts for documents that no longer exist in tenant state (deleted
// or never saved from another session). New-document draft slots (docId
// prefixed "new:") are exempt from the "still exists in state" check since
// they never appear in state.documents until their first successful save —
// they are still subject to the age check.
export function gcDrafts(tenantId, userId, validDocIds) {
  try {
    const prefix = scopePrefix(tenantId, userId);
    const validSet = new Set(validDocIds || []);
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(prefix)) continue;
      const docId = key.slice(prefix.length);
      let stale = true;
      try {
        const raw = localStorage.getItem(key);
        const draft = raw ? JSON.parse(raw) : null;
        const age = draft?.savedAt ? Date.now() - new Date(draft.savedAt).getTime() : Infinity;
        stale = age > MAX_AGE_MS || (!docId.startsWith('new:') && !validSet.has(docId));
      } catch (_e) {
        stale = true;
      }
      if (stale) toRemove.push(key);
    }
    toRemove.forEach((key) => localStorage.removeItem(key));
  } catch (_e) {
    /* ignore */
  }
}
